import React, { useState } from "react";
import { Button, Form, InputNumber, Modal, notification, Row } from "antd";
import { useLanguage } from "../../../../../../context/LanguageContext";
import Corridor from "../../../../../../models/Corridor";
import EmptyMap from "./modal/EmptyMap";
import SweepService from "../../../../../../services/SweepService";
import { EmptyPosition } from "../../../../../../models/Sweep";

interface ModalProps {
  isVisible: boolean;
  onClose: () => void;
  corridor: Corridor;
  sweepId: string;
  onUpdateContent: () => void;
}

const ModalComponent: React.FC<ModalProps> = ({
  isVisible,
  onClose,
  corridor,
  sweepId,
  onUpdateContent,
}) => {
  const { language } = useLanguage();
  const [form] = Form.useForm();
  const [emptyZone, setEmptyZone] = useState<EmptyPosition | null>(null);

  const handleCancel = () => {
    form.resetFields();
    setEmptyZone(null);
    onClose();
  };

  const handleConfirm = () => {
    Modal.confirm({
      title: language === "es" ? "¿Confirmar acción?" : "Confirm action?",
      content: language === "es"
        ? "¿Estás seguro de que querés selecionar las posiciones vacias?"
        : "Are you sure you want to select the empty positions??",
      okText: language === "es" ? "Acepto" : "Acept",
      cancelText: language === "es" ? "Cancelar" : "Cancel",
      okType: "primary",
      onOk: handleOk,
    });
  };

  const handleOk = async () => {
    if (emptyZone) {
      SweepService.setEmptyPosition(sweepId, corridor.name, emptyZone).then((res) => {
        if (res === "OK") {
          notification.success({
            message: language === "es" ? "Éxito" : "Success",
            description: language === "es"
              ? "Los cambios se veran reflejados en los proximos 30 segundos."
              : "The changes will be reflected in the next 30 seconds.",
          });
          handleCancel();
          onUpdateContent();
        } else {
          notification.error({
            message: language === "es" ? "Error" : "Error",
            description: language === "es"
              ? "error al setear las posiciones vacias"
              : "Failed to set empty position.",
          });
        }
      });
    }
  };

  return (
    <Modal
      title={`${language === "es" ? "Rack" : "Corridor"} ${corridor.name}`}
      centered
      open={isVisible}
      onCancel={handleCancel}
      width="80%"
      destroyOnClose
      footer={[
        <Button key="back" onClick={handleCancel} shape="round">
          {language === "es" ? "Cancelar" : "Cancel"}
        </Button>,
        <Button disabled={emptyZone === null ? true : false} key="submit" type="primary" onClick={handleConfirm} shape="round">
          {language === "es" ? "Enviar" : "Submit"}
        </Button>,
      ]}
    >
      {corridor.rack_template && (
        <>
          <EmptyMap
            rackTemplate={corridor.rack_template}
            emptyZone={emptyZone}
          />
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 20 }}>
            <Button
              onClick={() => {
                const template = corridor.rack_template;
                if (!template) return;
                const fullZone: EmptyPosition = {
                  col_from: template.column_range[0],
                  col_to: template.column_range[1],
                  level_from: template.level_range[0],
                  level_to: template.level_range[1],
                };
                setEmptyZone(fullZone);
                form.setFieldsValue({
                  fromCol: fullZone.col_from,
                  toCol: fullZone.col_to,
                  fromLvl: fullZone.level_from,
                  toLvl: fullZone.level_to,
                });
              }}
              shape="round"
              type="primary"
              style={{ marginRight: 10 }}
            >
              {language === "es" ? "Marcar todo vacío" : "Set all empty"}
            </Button>

            <Button
              onClick={() => {
                setEmptyZone(null);
                form.resetFields();
              }}
              danger
              type="primary"
              shape="round"
            >
              {language === "es" ? "Limpiar las vacias" : "Clear all empty"}
            </Button>
            <Form
              form={form}
              layout="inline"
              onFinish={({ fromLvl, toLvl, fromCol, toCol }) => {
                setEmptyZone({
                  col_from: fromCol,
                  col_to: toCol,
                  level_from: fromLvl,
                  level_to: toLvl,
                });
              }}
            >
              <Form.Item
                name="fromLvl"
                label={language === "es" ? `Desde nivel` : `From lvl`}
                rules={[{ required: true }]}
              >
                <InputNumber placeholder={corridor.rack_template.level_range[0].toString()} min={1} max={corridor.rack_template.level_range[1]} />
              </Form.Item>
              <Form.Item
                name="toLvl"
                label={language === "es" ? `Hasta nivel` : `To lvl`}
                rules={[{ required: true }]}
              >
                <InputNumber placeholder={corridor.rack_template.level_range[1].toString()} min={1} max={corridor.rack_template.level_range[1]} />
              </Form.Item>
              <Form.Item
                name="fromCol"
                label={language === "es" ? `Desde columna` : `From column`}
                rules={[{ required: true }]}
              >
                <InputNumber placeholder={corridor.rack_template.column_range[0].toString()} min={1} max={corridor.rack_template.column_range[1]} />
              </Form.Item>
              <Form.Item
                name="toCol"
                label={language === "es" ? `Hasta columna` : `To column`}
                rules={[{ required: true }]}
              >
                <InputNumber placeholder={corridor.rack_template.column_range[1].toString()} min={1} max={corridor.rack_template.column_range[1]} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  {language === "es" ? "Marcar zona vacía" : "Set empty zone"}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </>
      )}
    </Modal>
  );
};

export default ModalComponent;
